import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Building2, Users, FolderTree, Layers, Sparkles, ShieldCheck,
  Clock, Settings, Shield, UsersRound, QrCode, GitBranch, KeyRound,
  Calculator, ShieldCheck as ComplianceIcon, BookOpen, Activity,
  MonitorCheck, Wifi, Package, Bell, LayoutDashboard, ClipboardCheck, FileCheck,
  Plus, Loader2, Trash2, BarChart3, Mail, DollarSign, TrendingUp, History,
  ChevronLeft, ChevronRight, Heart, AlertTriangle, GraduationCap, Target, CreditCard, Key, Paintbrush, Fingerprint, Link2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import TenantManagement from './TenantManagement';
import UserManagementV2 from './UserManagementV2';
import RolesManager from './RolesManager';
import UserGroupsManager from './UserGroupsManager';
import PropertyGroups from './PropertyGroups';
// PropertyGroupManager is deprecated in favor of PropertyGroups (which now
// includes the Smart Rule Builder). Both admin-portal /groups and dashboard
// /groups tabs route to PropertyGroups for a single consistent experience.
import { AuditTrailViewer, BulkOperationsConsole } from './AdminFeatures';
import CrossVendorDupScanner from './CrossVendorDupScanner';
import AuditLogViewer from './AuditLogViewer';
import CorporateStack from './CorporateStack';
import OnboardingWizard from './OnboardingWizard';
import { ApprovalQueue } from './Portals';
import ActivityLog from './ActivityLog';
import SupportCardGenerator from './SupportCardGenerator';
import ChangeManagement from './ChangeManagement';
import KnowledgeBase from './KnowledgeBase';
import ComplianceTracker from './ComplianceTracker';
import { LicenseTracker, BudgetPlanner } from './LicenseAndBudget';
import SystemStatus from './SystemStatus';
import { NotificationCenter, SavedViews, AssetInventory, InfrastructureMap } from './EnterpriseFeatures';
import { IndustryBenchmarks, ScheduledReports, RoiCalculator, SpendAnalytics, ChangelogViewer } from './AnalyticsFeatures';
import { VendorHealth, IncidentLog, TrainingLinks, SlaTracking } from './VendorOps';
import BillingPage from './BillingPage';
import SsoSettings from './SsoSettings';
import WhiteLabelSettings from './WhiteLabelSettings';
import TwoFactorSettings from './TwoFactorSettings';
import IntegrationSettings from './IntegrationSettings';
import MyBilling from './MyBilling';

import SidebarLayout from './SidebarLayout';

const SECTION_GROUPS = [
  { label: 'My Organization', items: [
    { id: 'my-tenant', label: 'Tenant Settings', icon: Building2 },
    { id: 'billing', label: 'Billing & Plan', icon: CreditCard },
    { id: 'whitelabel', label: 'White Label', icon: Paintbrush },
    { id: 'sso', label: 'SSO Settings', icon: Key },
    { id: 'integrations', label: 'Integrations & API', icon: Link2 },
    { id: '2fa', label: 'Two-Factor Auth', icon: Fingerprint },
  ]},
  { label: 'People', items: [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'roles', label: 'Roles & Permissions', icon: Shield },
    { id: 'user-groups', label: 'User Groups', icon: UsersRound },
  ]},
  { label: 'Properties', items: [
    { id: 'groups', label: 'Property Groups', icon: FolderTree },
    { id: 'corporate-stack', label: 'Corporate Stack', icon: Layers },
    { id: 'onboarding-checklist', label: 'Onboarding Checklists', icon: ClipboardCheck },
    { id: 'onboarding', label: 'AI Property Wizard', icon: Sparkles },
  ]},
  { label: 'Governance', items: [
    { id: 'compliance', label: 'Compliance & Audit', icon: ComplianceIcon },
    { id: 'standards', label: 'Tech Standards', icon: FileCheck },
    { id: 'change-mgmt', label: 'Change Management', icon: GitBranch },
    { id: 'approvals', label: 'Approval Queue', icon: ShieldCheck },
  ]},
  { label: 'Communication', items: [
    { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
    { id: 'schedules', label: 'Scheduled Reports', icon: Mail },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'activity', label: 'Activity Log', icon: Clock },
    { id: 'audit-trail', label: 'Audit Trail', icon: History },
    { id: 'bulk-ops', label: 'Bulk Operations', icon: Layers },
    { id: 'dup-scan', label: 'Duplicate Detector', icon: GitBranch },
    { id: 'support-cards', label: 'Support Cards', icon: QrCode },
    { id: 'saved-views', label: 'Saved Views', icon: LayoutDashboard },
  ]},
];

export default function AdminPortal({ token, role, onSetTab }) {
  const [section, setSection] = useState('my-tenant');

  return (
    <SidebarLayout title="Admin" icon={Settings} groups={SECTION_GROUPS} activeId={section} onSelect={setSection}>
        {section === 'my-tenant' ? (
          <ScrollArea className="h-full">
            <div className="p-5 max-w-3xl space-y-4">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Tenant Settings</h2>
              <p className="text-xs text-muted-foreground">Your organization settings. Contact your LodgiFlow administrator to change plan features.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="border rounded-xl p-4 bg-card text-center">
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>—</div>
                  <div className="text-[10px] text-muted-foreground">Properties</div>
                </div>
                <div className="border rounded-xl p-4 bg-card text-center">
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>—</div>
                  <div className="text-[10px] text-muted-foreground">Users</div>
                </div>
                <div className="border rounded-xl p-4 bg-card text-center">
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>—</div>
                  <div className="text-[10px] text-muted-foreground">Plan</div>
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'users' ? (
          <UserManagementV2 token={token} />
        ) : section === 'billing' ? (
          <MyBilling token={token} />
        ) : section === 'roles' ? (
          <RolesManager token={token} />
        ) : section === 'user-groups' ? (
          <UserGroupsManager token={token} />
        ) : section === 'notifications' ? (
          <NotificationCenter token={token} />
        ) : section === 'saved-views' ? (
          <SavedViews token={token} />
        ) : section === 'sso' ? (
          <SsoSettings token={token} />
        ) : section === 'whitelabel' ? (
          <WhiteLabelSettings token={token} />
        ) : section === '2fa' ? (
          <TwoFactorSettings token={token} />
        ) : section === 'integrations' ? (
          <IntegrationSettings token={token} />
        ) : section === 'change-mgmt' ? (
          <ChangeManagement token={token} />
        ) : section === 'onboarding-checklist' ? (
          <OnboardingChecklistView token={token} />
        ) : section === 'compliance' ? (
          <ComplianceTracker token={token} />
        ) : section === 'knowledge' ? (
          <KnowledgeBase token={token} />
        ) : section === 'standards' ? (
          <TechStandardsView token={token} />
        ) : section === 'schedules' ? (
          <ScheduledReports token={token} />
        ) : section === 'support-cards' ? (
          <SupportCardGenerator token={token} />
        ) : section === 'groups' ? (
          <PropertyGroups token={token} role={role} />
        ) : section === 'corporate-stack' ? (
          <CorporateStack token={token} role={role} />
        ) : section === 'onboarding' ? (
          <div className="p-4">
            <OnboardingWizard token={token} onComplete={() => setSection('my-tenant')} />
          </div>
        ) : section === 'approvals' ? (
          <ApprovalQueue token={token} />
        ) : section === 'activity' ? (
          <ActivityLog token={token} />
        ) : section === 'audit-trail' ? (
          <AuditLogViewer token={token} />
        ) : section === 'bulk-ops' ? (
          <BulkOperationsConsole token={token} />
        ) : section === 'dup-scan' ? (
          <CrossVendorDupScanner token={token} />
        ) : null}
    </SidebarLayout>
  );
}


// ── Inline sub-components ──

function OnboardingChecklistView({ token }) {
  const [checklists, setChecklists] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createPropId, setCreatePropId] = React.useState('');
  const [properties, setProperties] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  React.useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/onboarding-checklists`, { headers }),
      axios.get(`${BACKEND_URL}/api/properties`, { headers }),
    ]).then(([c, p]) => { setChecklists(c.data.checklists || []); setProperties(p.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [headers]);

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/onboarding-checklists`, { property_id: createPropId }, { headers });
      toast.success('Checklist created');
      setCreateOpen(false);
      const res = await axios.get(`${BACKEND_URL}/api/onboarding-checklists`, { headers });
      setChecklists(res.data.checklists || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleItem = async (checklistId, idx, completed) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/onboarding-checklists/item`, { checklist_id: checklistId, item_index: idx, completed }, { headers });
      const res = await axios.get(`${BACKEND_URL}/api/onboarding-checklists`, { headers });
      setChecklists(res.data.checklists || []);
      const updated = res.data.checklists?.find(c => c.id === checklistId);
      if (updated) setSelected(updated);
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="flex h-full" data-testid="onboarding-checklists">
      <div className="w-64 border-r bg-muted/20 flex flex-col shrink-0">
        <div className="p-4 border-b"><div className="flex items-center justify-between"><h3 className="text-sm font-semibold">Checklists</h3><Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} className="h-7 gap-1 text-xs" data-testid="create-checklist-btn"><Plus className="h-3 w-3" /> New</Button></div></div>
        <ScrollArea className="flex-1"><div className="p-2 space-y-1">
          {checklists.map(c => (
            <button key={c.id} onClick={() => setSelected(c)} className={`w-full text-left p-3 rounded-lg transition-colors text-sm ${selected?.id === c.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'}`}>
              <div className="font-medium truncate">{c.property_name}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-muted-foreground">{c.completed_items}/{c.total_items}</span>
                <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${c.completion_pct}%` }} /></div>
                <span className="text-[10px] font-mono">{c.completion_pct}%</span>
              </div>
            </button>
          ))}
          {checklists.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No checklists yet</p>}
        </div></ScrollArea>
      </div>
      <div className="flex-1 overflow-auto">
        {selected ? (
          <div className="p-6 max-w-2xl">
            <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>{selected.property_name}</h2>
            <p className="text-sm text-muted-foreground mb-4">{selected.completed_items} of {selected.total_items} tasks completed ({selected.completion_pct}%)</p>
            <div className="space-y-1">
              {(selected.items || []).map((item, idx) => (
                <label key={idx} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${item.completed ? 'bg-emerald-50/50 border-emerald-200' : 'hover:bg-muted/40'}`}>
                  <Checkbox checked={item.completed} onCheckedChange={(v) => toggleItem(selected.id, idx, v)} />
                  <div className="flex-1">
                    <span className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : 'font-medium'}`}>{item.task}</span>
                    <Badge variant="outline" className="ml-2 text-[8px]">{item.category}</Badge>
                  </div>
                  <Badge className={`text-[8px] ${item.priority === 'critical' ? 'bg-red-100 text-red-700' : item.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{item.priority}</Badge>
                </label>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground"><div className="text-center"><ClipboardCheck className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">Select a checklist to manage</p></div></div>
        )}
      </div>
      <Dialog open={createOpen} onOpenChange={setCreateOpen}><DialogContent className="max-w-sm"><DialogHeader><DialogTitle>New Onboarding Checklist</DialogTitle></DialogHeader><div className="py-2"><label className="text-sm font-medium">Property</label><Select value={createPropId} onValueChange={setCreatePropId}><SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div><DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={create} disabled={saving || !createPropId}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Create</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}

function TechStandardsView({ token }) {
  const [standards, setStandards] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', property_type: 'hotel', service_level: '', platform_type_ids: [], description: '' });
  const [platforms, setPlatforms] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  React.useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/tech-standards`, { headers }),
      axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
    ]).then(([s, p]) => { setStandards(s.data.standards || []); setPlatforms(p.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [headers]);

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tech-standards`, form, { headers });
      toast.success('Standard created');
      setCreateOpen(false);
      const res = await axios.get(`${BACKEND_URL}/api/tech-standards`, { headers });
      setStandards(res.data.standards || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const togglePlatform = (pid) => setForm(p => ({ ...p, platform_type_ids: p.platform_type_ids.includes(pid) ? p.platform_type_ids.filter(x => x !== pid) : [...p.platform_type_ids, pid] }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="tech-standards">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Technology Standards Library</h2><p className="text-sm text-muted-foreground">Define approved tech stacks by property type and service level. Measure compliance against your standards.</p></div>
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} className="gap-1 text-xs" data-testid="create-standard-btn"><Plus className="h-3 w-3" /> New Standard</Button>
      </div>
      {standards.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground"><FileCheck className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No standards defined yet</p><p className="text-xs mt-1">Create a standard to define the approved platforms for a property type</p></div>
      ) : (
        <div className="space-y-3">
          {standards.map(s => (
            <div key={s.id} className="p-4 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <div><span className="font-semibold text-sm">{s.name}</span><Badge variant="outline" className="ml-2 text-[9px] capitalize">{s.property_type} {s.service_level && `/ ${s.service_level}`}</Badge></div>
                <Badge variant="secondary" className="text-[10px]">{s.platform_count} platforms</Badge>
              </div>
              {s.description && <p className="text-xs text-muted-foreground mb-2">{s.description}</p>}
              <div className="flex flex-wrap gap-1">{(s.platforms || []).map(p => <Badge key={p.id} variant="outline" className="text-[9px]">{p.name}</Badge>)}</div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}><DialogContent className="max-w-lg max-h-[85vh] flex flex-col"><DialogHeader><DialogTitle>New Technology Standard</DialogTitle></DialogHeader><div className="space-y-3 py-2 flex-1 overflow-auto">
        <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Luxury Hotel Standard" className="mt-1" data-testid="standard-name" /></div>
        <div className="grid grid-cols-2 gap-3"><div><label className="text-sm font-medium">Property Type</label><Select value={form.property_type} onValueChange={v => setForm(p => ({ ...p, property_type: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hotel">Hotel</SelectItem><SelectItem value="restaurant">Restaurant</SelectItem><SelectItem value="entertainment">Entertainment</SelectItem><SelectItem value="retail">Retail</SelectItem></SelectContent></Select></div><div><label className="text-sm font-medium">Service Level</label><Select value={form.service_level} onValueChange={v => setForm(p => ({ ...p, service_level: v }))}><SelectTrigger className="mt-1"><SelectValue placeholder="Any" /></SelectTrigger><SelectContent><SelectItem value="">Any</SelectItem><SelectItem value="luxury">Luxury</SelectItem><SelectItem value="full">Full Service</SelectItem><SelectItem value="select">Select Service</SelectItem><SelectItem value="economy">Economy</SelectItem></SelectContent></Select></div></div>
        <div><label className="text-sm font-medium">Required Platforms ({form.platform_type_ids.length})</label><ScrollArea className="mt-1 max-h-[200px] border rounded-lg p-2"><div className="space-y-1">{platforms.map(p => (
          <label key={p.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/40 cursor-pointer text-sm"><Checkbox checked={form.platform_type_ids.includes(p.id)} onCheckedChange={() => togglePlatform(p.id)} />{p.name}</label>
        ))}</div></ScrollArea></div>
        <div><label className="text-sm font-medium">Description</label><Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="All luxury properties should have these platforms" className="mt-1" /></div>
      </div><DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={create} disabled={saving || !form.name || form.platform_type_ids.length === 0}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Create Standard</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}
